<script setup lang="ts">
import { useAuth } from '../../composables/useAuth';

const props = defineProps<{ mode: 'ships' | 'pos'; posBuilderEnabled: boolean }>();

const { isLoggedIn, characterName, characterId, login, logout } = useAuth();

const emit = defineEmits<{
  import: [];
  export: [];
  'eve-sync': [];
  'change-mode': [mode: 'ships' | 'pos'];
}>();
</script>

<template>
  <header class="app-header">
    <div class="header-left">
      <h1 class="app-title">EveFit</h1>
      <span class="app-subtitle">{{ props.mode === 'pos' ? 'POS Builder' : 'Ship Fitting Tool' }}</span>
    </div>

    <nav class="mode-switch" aria-label="EveFit mode">
      <button :class="{ active: props.mode === 'ships' }" :aria-current="props.mode === 'ships' ? 'page' : undefined" @click="emit('change-mode', 'ships')">Ship Fits</button>
      <button v-if="props.posBuilderEnabled" :class="{ active: props.mode === 'pos' }" :aria-current="props.mode === 'pos' ? 'page' : undefined" @click="emit('change-mode', 'pos')">POS Builder</button>
    </nav>

    <div v-if="props.mode === 'ships'" class="header-center">
      <button class="header-action" @click="emit('import')" title="Import EFT fitting">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 1v8M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M1 11h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
        </svg>
        Import
      </button>
      <button class="header-action" @click="emit('export')" title="Export EFT fitting">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 9V1M3 5l4-4 4 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M1 11h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
        </svg>
        Export
      </button>
      <button v-if="isLoggedIn" class="header-action" @click="emit('eve-sync')" title="Sync with EVE character">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 7a6 6 0 0 1 11-3M13 7a6 6 0 0 1-11 3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
          <path d="M12 1v3h-3M2 13v-3h3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        EVE Sync
      </button>
    </div>
    <div v-else class="header-center header-center--pos">Legacy starbase planning</div>

    <div class="header-right">
      <template v-if="isLoggedIn">
        <img
          v-if="characterId"
          :src="`https://images.evetech.net/characters/${characterId}/portrait?size=32`"
          class="char-portrait"
          :alt="characterName ?? ''"
        />
        <span class="char-name">{{ characterName }}</span>
        <button class="header-btn" @click="logout">Logout</button>
      </template>
      <template v-else>
        <button class="header-btn header-btn--primary" @click="login">
          Login with EVE
        </button>
      </template>
    </div>
  </header>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 58px;
  padding: 10px 16px;
  background:
    linear-gradient(90deg, rgba(0, 212, 255, 0.075), transparent 34%),
    linear-gradient(180deg, var(--bg-panel), var(--bg-darker));
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 1px 0 rgba(0, 212, 255, 0.12), var(--shadow-sm);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  flex-direction: column;
  min-width: 150px;
  line-height: 1.05;
}

.app-title {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 1.08rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: var(--text-primary);
  text-transform: uppercase;
  text-shadow: 0 0 14px rgba(0, 212, 255, 0.22);
}

.app-subtitle {
  margin-top: 4px;
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent-blue);
}

.mode-switch,
.header-center,
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.mode-switch {
  display: flex;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.mode-switch button {
  padding: 7px 11px;
  border: 0;
  background: linear-gradient(180deg, var(--bg-raised), var(--bg-secondary));
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
}

.mode-switch button.active {
  background: rgba(0, 212, 255, 0.16);
  color: var(--text-primary);
  box-shadow: inset 0 -2px var(--accent-blue);
}

.header-center--pos {
  color: var(--accent-blue);
  font-family: var(--font-mono);
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.header-right {
  justify-content: flex-end;
  min-width: 180px;
}

.header-action,
.header-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 11px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: linear-gradient(180deg, var(--bg-raised), var(--bg-secondary));
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color var(--transition-fast), color var(--transition-fast), box-shadow var(--transition-fast), transform var(--transition-fast);
}

.header-action:hover,
.header-btn:hover {
  color: var(--text-primary);
  border-color: var(--border-accent);
  box-shadow: var(--glow-blue);
  transform: translateY(-1px);
}

.header-btn--primary {
  color: #001018;
  border-color: rgba(0, 212, 255, 0.7);
  background: linear-gradient(180deg, #4de6ff, var(--accent-blue));
}

.header-btn--primary:hover {
  color: #001018;
}

.char-portrait {
  width: 32px;
  height: 32px;
  border: 1px solid var(--border-accent);
  border-radius: 50%;
  box-shadow: var(--glow-blue);
}

.char-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-primary);
  font-size: 0.82rem;
}

@media (max-width: 900px) {
  .app-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .mode-switch,
  .header-center,
  .header-right {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
